<template>

  <ion-page id="enginemenu-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />

      </div>

      <div v-show="showState == SHOW_MENU">
        <TopLine :style="styleTopline" :title="topLineTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
        <ion-list id="engine-menu-list" :style="styleMiddle">
          <ion-item v-for="item in menuItems" v-bind:key="item.index" @click="onClick(item.index)">
            <ion-label>{{ item.label }}</ion-label>
          </ion-item>
          <ion-item class="text-message">
            <ion-label color="danger">
              {{ textMessage }}
            </ion-label>
           </ion-item>
        </ion-list>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';

  import { computed } from "@vue/reactivity"
  import { ref, watch } from 'vue'
  import ToolBar from '@/components/ToolBar.vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import { doStartupCheck } from '@/composables/startupCheck.js'
  import TopLine from '@/components/TopLine.vue'
  import { useGetData, generateErrorAlertText } from '../../composables/getData.js'
  import { parseWordsSaid , buildWordListFromChoiceList } from '@/composables/wordlistSupport.js'
  import { translateEnglishWords, translatePhrase } from '@/composables/translation';

  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import ExplainTour from '@/components/ExplainTour.vue'

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()  
  const windowSizeStore = useWindowSizeStore()

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const isDark = ref(connectionStore.getDarkmode != "light")
  var isSubscriber = connectionStore.getIsSubscriber

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  const topLineTitle = ref(translatePhrase("Engines Menu"))

  const wordlistExplain = "command done"
  const wordlistTrigger = ref(0)

  const wordlistMenu = "command scroll up down done explain ignore play computer blindfold game history continue previous mute listen";
  
  const wordlist = ref(wordlistMenu)
  emit("word-list",wordlist.value)

  const subscriberMessage = translatePhrase("Thanks for being a Subscriber! -- You have access to both Komodo & Stockfish Engines at all skill levels. Plus All Blindfold Stepping Stones")
  const freeMessage = translatePhrase("Your free account gives access to the Komodo Engine at its easiest setting only. For blindfold chess, you'll have two 'stepping stone' levels. See the 'Subscribe' screen for all the subscriber benefits")

  const textMessage = ref((isSubscriber == 1) ? subscriberMessage : freeMessage)

  const stringsOpenGame = [
          translateEnglishWords("Continue Previous Game"),
          translateEnglishWords("Play Computer"), 
          translateEnglishWords("Play Blindfold"), 
          translateEnglishWords("Game History")
        ]
  const stringsNoOpenGame = [
          translateEnglishWords("Play Computer"), 
          translateEnglishWords("Play Blindfold"), 
          translateEnglishWords("Game History")
        ]

  const menuKeyWordsOpenGame = ["continue","computer", "blindfold", "history"]
  const menuKeyWordsNoOpenGame = ["computer", "blindfold", "history"]

  const SHOW_MENU = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_MENU)

  const TOOLBAR_EXPLAIN = 0

  const toolbarElements = [
    { index : TOOLBAR_EXPLAIN, label : translateEnglishWords("Explain") ,disabled : false},
  ]

  const toolbarTrigger = ref(0)

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  var openGameId = connectionStore.getPlayComputerGameId
  const menuItems = ref([])

  buildMenu()
  showState.value = SHOW_MENU

  doStartupCheck(false,afterStartupCallbackRoutine)

  watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
  })

  function afterStartupCallbackRoutine(stuffChanged) {
    //getOpenGame()
  }

  function buildMenu() {
    menuItems.value = []

    const useStrings = (openGameId > 0) ? stringsOpenGame : stringsNoOpenGame
    for (var i=0; i < useStrings.length; i++)
      menuItems.value.push( { index : i, label : useStrings[i]})
  }

  function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  // START Explain Tour Routines

  function explainTourInitialize(errorText) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? "engine" : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {

      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_MENU
        wordlist.value = wordlistMenu
        emit("word-list",wordlist.value)
        wordlistTrigger.value++
        break
    }
  }

  // END Explain Tour Routines

  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_EXPLAIN :
        processWords("command explain")
        break
    }
  }

  function onClick(index) {
    if (openGameId > 0)
      processWords("command " + menuKeyWordsOpenGame[index])
    else
      processWords("command " + menuKeyWordsNoOpenGame[index])
   
  }

  function processWords(text) {
    //console.log("text in engine Menu ",text)
    var returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)

    if (showState.value == SHOW_EXPLAINTOUR) {
      //console.log("*** words for explain tour",text)
      explainTourWordsSpoken.value = text
      explainTourWordsSpokenTrigger.value++
      return;
    }
    switch (returnArray[0]) {
    case "scroll" :
      scrollElement("engine-menu-list",returnArray[1])
      break
    case "mute" :
      microphoneIsMuted.value = true 
      emit("mic-is-muted",microphoneIsMuted.value)
      connectionStore.setMicrophoneIsMuted(true)
      break
    case "listen" :
      microphoneIsMuted.value = false 
      emit("mic-is-muted",microphoneIsMuted.value)
      connectionStore.setMicrophoneIsMuted(false)
      break
      case "explain" :
        explainTourInitialize(null)
        break;
      case "done" :
        emit("where-next","/")
        break;
      case "continue" :
        connectionStore.setPlayType("continue")
        emit("where-next","/verbalplay")
        break
      case "computer" :
        connectionStore.setPlayType("computer")
        emit("where-next","/verbalplay")
        break
      case "blindfold" :
        connectionStore.setPlayType("blindfold")
        emit("where-next","/verbalplay")
        break
      case "history" :
        emit("where-next","/gamehistory")
        break
    }
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain" || currentWord == "mute" || currentWord == "listen") {
            return [currentWord,""]
        }
        if (currentWord == "done") {
            return ["done",""]
        }
        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
      
        if (openGameId > 0 && 
                (currentWord == "continue" || currentWord == "previous")) {
          return (["continue",""])
        }
        if (currentWord == "computer") {
            return ["computer",""]
        }
        if (currentWord == "blindfold") {
            return ["blindfold",""]
        }
        if (currentWord == "game" || currentWord == "history" ) {
            return ["history",""]
        }
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function processJsonResponse (json) {
    //console.log(" in engine menu callback",json)
    if (json.hasOwnProperty('error')) {
      // showState.value = SHOW_EXPLAINTOUR
      // explainTitle.value = "*** ERROR ALERT ***"
      // explainMessage.value =
      explainTourInitialize(generateErrorAlertText(json))
      return;
    }
    openGameId = json['gameid']
    
    buildMenu()
    showState.value = SHOW_MENU
  }
  
  function getOpenGame() {
    var formData = {
      verbal : 1
    }

    useGetData("get18_game_in_progress.php", formData, processJsonResponse)
  }

</script>

<style scoped>

ion-label {
    cursor: pointer;
    margin: 0px;
    padding : 0px;
 }

 ion-item.text-message {
    margin-top: 0px; 

    cursor: pointer;
    padding : 0px;
 }
</style>